import React, { useState, useEffect, useCallback, useRef } from "react";
import { Col, Container, Row, Card, CardBody } from "reactstrap";
import Select from "react-select";
import axios from "axios";
import { Button, Form } from "react-bootstrap";
import { useHistory } from "react-router-dom";
import config from "../../../../config";
import { toast } from "react-toastify";
import Breadcrumbs from "../../../Helper/Breadcrumbs";
import { toastAttr } from "../../OfflineLeads/utils";
import EditorJsComponent from "../blogdataform/EditorJsComponent";

const tokenHeader = () => ({
  headers: { Authorization: "Token " + sessionStorage.getItem("tokenKey") },
});

const MandapContentForm = (props) => {
  const history = useHistory();
  const propsState = props.history?.location?.state;
  const editMode = (propsState && propsState.editMode) || false;

  const [mandaps, setMandaps] = useState([]);
  const [isLoadingMandaps, setIsLoadingMandaps] = useState(false);
  const [nextUrl, setNextUrl] = useState(null);
  const [selectedMandap, setSelectedMandap] = useState(
    editMode && propsState?.mandap_id
      ? { value: propsState.mandap_id, label: propsState.mandap_name }
      : null
  );
  const [htmlContent, setHtmlContent] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [hasExistingContent, setHasExistingContent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const editorRef = useRef(null);

  const breadcrumbData = [
    { title: "seobook", href: "/seobook" },
    { title: "Mandap Content", href: "/seobook/mandapcontent" },
    { title: editMode ? "Edit Content" : "Create Content", href: "#" },
  ];

  const fetchMandaps = useCallback(
    async (url = `${config.service}/masters/guest/vendor_mandap_all/`) => {
      setIsLoadingMandaps(true);
      try {
        const resp = await axios.get(url, tokenHeader());
        const results = Array.isArray(resp.data) ? resp.data : resp.data.results || [];
        setMandaps((prev) => [
          ...prev,
          ...results.map((m) => ({ value: m.id, label: m.mandap_name })),
        ]);
        setNextUrl(resp.data.next || null);
      } catch (err) {
        toast.error("Failed to load mandaps", toastAttr);
      } finally {
        setIsLoadingMandaps(false);
      }
    },
    []
  );

  useEffect(() => {
    fetchMandaps();
  }, [fetchMandaps]);

  useEffect(() => {
    if (!selectedMandap) {
      setHtmlContent("");
      setIsActive(true);
      setHasExistingContent(false);
      return;
    }
    let cancel = false;
    const loadContent = async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(
          `${config.service}/masters/vendor_mandap/${selectedMandap.value}/content/`,
          tokenHeader()
        );
        if (cancel) return;
        setHtmlContent(data.html_content || "");
        setIsActive(data.is_active ?? true);
        setHasExistingContent(true);
      } catch (err) {
        if (cancel) return;
        if (err.response?.status === 404) {
          setHtmlContent("");
          setIsActive(true);
          setHasExistingContent(false);
        } else {
          toast.error("Failed to load mandap content", toastAttr);
        }
      } finally {
        if (!cancel) setLoading(false);
      }
    };
    loadContent();
    return () => (cancel = true);
  }, [selectedMandap]);

  const handleEditorChange = (e) => {
    setHtmlContent(e.target.value);
  };

  const handleSave = async () => {
    if (!selectedMandap) {
      toast.error("Please select a mandapam first.", toastAttr);
      return;
    }
    setSaving(true);
    try {
      // Pull the editor's current content directly rather than trusting
      // `htmlContent`, which only updates via EditorJsComponent's onChange —
      // see EditorJsComponent's `save()`.
      const freshContent = await editorRef.current?.save();
      const contentToSave = freshContent != null ? freshContent : htmlContent;
      await axios.put(
        `${config.service}/masters/vendor_mandap/${selectedMandap.value}/content/`,
        { html_content: contentToSave, is_active: isActive },
        tokenHeader()
      );
      setHasExistingContent(true);
      toast.success("Content saved successfully", toastAttr);
      history.push("/seobook/mandapcontent");
    } catch (err) {
      toast.error(
        `Save failed: ${err.response?.data?.error || err.message}`,
        toastAttr
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedMandap || !hasExistingContent) return;
    if (!window.confirm("Delete this mandapam's custom content? The page will fall back to the auto-generated description.")) {
      return;
    }
    setDeleting(true);
    try {
      await axios.delete(
        `${config.service}/masters/vendor_mandap/${selectedMandap.value}/content/`,
        tokenHeader()
      );
      toast.success("Content deleted. Reverted to the default template.", toastAttr);
      history.push("/seobook/mandapcontent");
    } catch (err) {
      toast.error("Delete failed", toastAttr);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Container className="vendor">
      <Row>
        <Col md={12} className="contBody">
          <Card>
            <CardBody className="theme-light">
              <div className="card__title pageNav">
                <div className="bold-text">
                  <Breadcrumbs data={breadcrumbData} />
                </div>
              </div>

              <Row className="formBtnsDiv mb-3">
                <Col md={12}>
                  <Button variant="primary" onClick={handleSave} disabled={saving || !selectedMandap}>
                    {saving ? "Saving..." : "Save Content"}
                  </Button>{" "}
                  {hasExistingContent && (
                    <Button variant="danger" onClick={handleDelete} disabled={deleting || !selectedMandap}>
                      {deleting ? "Deleting..." : "Delete Content"}
                    </Button>
                  )}{" "}
                  <Button variant="secondary" onClick={() => history.push("/seobook/mandapcontent")}>
                    Cancel
                  </Button>
                </Col>
              </Row>

              <div style={{ maxWidth: 900 }}>
                <Select
                  options={mandaps}
                  value={selectedMandap}
                  onChange={setSelectedMandap}
                  isClearable
                  isDisabled={editMode}
                  isLoading={isLoadingMandaps}
                  placeholder="Select a mandapam..."
                  onMenuScrollToBottom={() => nextUrl && fetchMandaps(nextUrl)}
                  styles={{
                    container: (base) => ({ ...base, marginBottom: 20 }),
                    control: (base) => ({ ...base, backgroundColor: "#fff" }),
                    menu: (base) => ({ ...base, backgroundColor: "#fff", zIndex: 20 }),
                    singleValue: (base) => ({ ...base, color: "#333" }),
                  }}
                />

                {selectedMandap && (
                  <Form.Check
                    type="switch"
                    id="mandap-content-active"
                    label={isActive ? "Active (shown on the entity page)" : "Inactive (falls back to auto-generated description)"}
                    checked={isActive}
                    onChange={(e) => setIsActive(e.target.checked)}
                    style={{ marginBottom: 12 }}
                  />
                )}

                <div style={{ position: "relative" }}>
                  {loading && (
                    <div
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: "rgba(255,255,255,0.8)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        zIndex: 10,
                      }}
                    >
                      Loading content...
                    </div>
                  )}
                  <EditorJsComponent
                    ref={editorRef}
                    data={htmlContent}
                    onChange={handleEditorChange}
                    name="html_content"
                    placeholder="Write the venue description shown on the entity page..."
                  />
                </div>

                {!hasExistingContent && selectedMandap && !loading && (
                  <p style={{ marginTop: 10, color: "#6c757d" }}>
                    No custom content yet — the entity page is currently showing the
                    auto-generated description.
                  </p>
                )}
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default MandapContentForm;
